import { useAuth0 } from '@auth0/auth0-react';
import { DescriptionOutlined, ExpandLess, ExpandMore, HolidayVillage, House, Settings } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';
import Icon from '@mui/material/Icon';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Divider, Drawer, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, useMediaQuery } from '@mui/material';
import List from '@mui/material/List';
import { capitalize, uniq } from 'lodash';
import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { track, useContent } from '../../logic';
import { useUserConfig } from '../../logic/useUserConfig';
import { humanize, toTitleCase } from '../../utils';
import { useOrganization } from '../OrganizationProvider';
import { RoleDetail } from './ClosingHeader';
import { Legalese } from './Legalese';
import { PortalDomainOrg, PortalOrgUiConfigLink } from '../../types';
import { SmsPreferenceStatusDialog } from './SmsPreferenceStatusDialog';
import { NotificationPreferenceStatusDialog } from './NotificationPreferenceStatusDialog';
import { useExternalLinkDialog } from './useExternalLinkDialog';
import { useGetOrder } from '../../logic/useApiQuery';
import { useAppTheme } from '../../logic/useAppTheme';
import { usePinTokenState } from '../context/pin-state/PinState';
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder';
import { useIsPinPage } from '../../logic/useIsPinPage';

export function AppDrawer({ drawerWidth, drawerHeight }: {
	drawerWidth: number;
	drawerHeight: number;
}) {

  const org = useOrganization()
  const theme = useAppTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const { isAuthenticated } = useAuth0()
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { pinToken } = usePinTokenState()
  const isPinPage = useIsPinPage()

  const isAuthenticatedAndNotPinPage = isAuthenticated && !isPinPage
  const isAuthenticatedOrPin = isAuthenticated || !!pinToken
  const pinPageWithNoPin = isPinPage && !pinToken
  const isNotPinPageAndNotAuthenticated = !isPinPage && !isAuthenticated

  const { data: userConfig, error: userConfigError } = useUserConfig(isAuthenticatedAndNotPinPage)
  let { RoleOptions = [] } = globalOrderData || {}
  const showNotificationsMenuItem = globalOrderData && !isAdhocPortalOrder(globalOrderData.Order)
  const multipleRoles = RoleOptions?.length > 1

  const links = userConfig?.uiConfig?.Links || []

  useEffect(() => {
    if (userConfigError) {
      track('error', 'useUserConfig', {}, {
        message: JSON.stringify(userConfigError),
        component: 'AppDrawer',
      })
    }
  }, [userConfigError])
  const { ClosingsText, ClosingText } = useContent()

  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [smsPreferenceStatusOpen, setSmsPreferenceStatusOpen] = useState(false)
  
  useEffect(() => {
    if (!globalOrderData || isAdhocPortalOrder(globalOrderData.Order)) return
    const sms = globalOrderData.Order.SmsPreferenceStatus
    const needsConsent = org.SmsOptInRequired && (!sms || sms.consent == null)
    if (needsConsent) {
      setSmsPreferenceStatusOpen(true)
    }
  }, [globalOrderData, org.SmsOptInRequired])

  const embeddedLink = useExternalLinkDialog({
    fullScreen: isSmall,
  })

  const openLink = (link: PortalOrgUiConfigLink) => {
    if (link.type === 'external') {
      window.open(link.url, '_blank', 'noopener,noreferrer')
      return
    }
    embeddedLink.open(link.url, { label: link.label })
  }

  const renderLinks = (
    links.map(link => {
      const iconEl = link.icon?.value
        ? <Icon className="material-icons-outlined">{link.icon.value}</Icon>
        : undefined;
      return (
        <Button
          key={link.url}
          onClick={() => openLink(link as any)}
          variant="contained"
          sx={{
            width: '100%',
            height: 'auto',
            minHeight: '39px',
            boxShadow: 'none !important',
            display: 'flex',
            justifyContent: 'space-between',
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: '400',
            mt: 2,
            border: '1px solid #FFFFFF',
            '&:hover': {
              bgcolor: 'primary.light',
              color: 'white',
              borderColor: 'primary.light',
              '.MuiButton-endIcon': { color: 'white' },
            },
            '.MuiButton-endIcon': {
              color: '#FFFFFF',
              '& .MuiSvgIcon-root': { fontSize: '24px' },
            },
          }}
          endIcon={iconEl}
        >
          <Box
            component="span"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              textAlign: 'left',
            }}
          >
            {link.label}
          </Box>
        </Button>
      );
    })
  );

  const topNav = (
    <Box
      component="nav"
    >
      <Drawer
        variant="permanent"
        anchor='top'
        sx={{
          '& .MuiDrawer-paper': {
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'border-box',
            width: '100%',
            height: drawerHeight,
            bgcolor: 'white',
            borderBottom: 'none',
          },
        }}
        open
      >
        <Toolbar sx={{
          height: '100%',
          width: '100%',
          ml: '69px',
          p: '5px',
        }}>
          <CompanyLogo org={org} />
        </Toolbar>
        {isAuthenticatedOrPin ?
          <Box sx={{ flex: 1, textAlign: 'right' }} >
            <HamburgerMenu dynamicLinks={links} onOpenLink={openLink}></HamburgerMenu>
          </Box> :
          <Box sx={{ flex: 1, minWidth: '69px', }} ></Box>
        }
      </Drawer>
    </Box>
  )

  const leftNav = (
    <>
      <Box
        component="nav"
        sx={{
          width: drawerWidth,
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="permanent"
          anchor='left'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '& .MuiDrawer-paper': {
              border: 'none',
              width: drawerWidth,
              // padding: '0 10px',
              boxSizing: 'border-box',
              bgcolor: 'primary.main',
            },
          }}
          open
        >
          <Box sx={{
            backgroundColor: 'white',
            borderBottomLeftRadius: '20px',
          }}>
            <Toolbar sx={{
              display: 'flex',
              justifyContent: 'center',
              margin: '35px 0',
              height: '80px',
            }}>
              <CompanyLogo org={org} />
            </Toolbar>

          </Box>
          <Box sx={{ backgroundColor: 'white' }}>
            <Box sx={{
              p: '0 10px', textAlign: 'center', backgroundColor: 'primary.main',
              borderTopRightRadius: '20px',
            }}>
              <List sx={{ mb: 3 }}>
                {isAuthenticatedAndNotPinPage &&
                  <Button
                    component={NavLink}
                    to="/closings"
                    variant="contained"
                    sx={{
                      width: '100%', height: '39px', boxShadow: 'none !important',
                      display: 'flex', justifyContent: 'space-between', color: '#FFFFFF',
                      fontSize: '16px', fontWeight: '400', mt: '40px', border: '1px solid #FFFFFF',
                      '&.active': { bgcolor: 'white', border: 'none', color: '#000000', '.MuiButton-endIcon': { color: 'primary.main' },  },
                      '&:hover': { bgcolor: 'primary.light', color: 'white', borderColor: 'primary.light', '.MuiButton-endIcon': { color: 'white' }, },
                      '.MuiButton-endIcon': { color: '#FFFFFF', '& .MuiSvgIcon-root': { fontSize: '24px' } },
                    }}
                    endIcon={<HolidayVillage />}
                    end={true}
                  >
                    <Box component="span" sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', textAlign: 'left' }}>
                      {capitalize(ClosingsText)}
                    </Box>
                  </Button>
                }

                {closingId && isAuthenticatedAndNotPinPage &&
                  <Button
                    component={NavLink}
                    to={`/closings/${closingId}`}
                    variant="contained"
                    sx={{
                      width: '100%', height: 'auto', minHeight: '39px', boxShadow: 'none !important',
                      display: 'flex', justifyContent: 'space-between', color: '#FFFFFF',
                      fontSize: '16px', fontWeight: '400', mt: 2, border: '1px solid #FFFFFF',
                      '&.active': { bgcolor: 'white', border: 'none', color: '#000000', '.MuiButton-endIcon': { color: 'primary.main' },  },
                      '&:hover': { bgcolor: 'primary.light', color: 'white', borderColor: 'primary.light', '.MuiButton-endIcon': { color: 'white' }, },
                      '.MuiButton-endIcon': { color: '#FFFFFF', '& .MuiSvgIcon-root': { fontSize: '24px' } },
                    }}
                    endIcon={<House />}
                    end={true}
                  >
                    <Box component="span" sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', textAlign: 'left' }}>
                      {capitalize(ClosingText)} Overview
                    </Box>
                  </Button>
                }
                { renderLinks }
              </List>
              <List>
                {(isAuthenticatedAndNotPinPage && multipleRoles) ?
                  <Box sx={{mt: 1}}>
                    <RoleDetail globalOrderData={globalOrderData} />
                  </Box> : ''
                }

                {showNotificationsMenuItem ?
                  <Box sx={{mt: 1}}>
                    <Button
                      onClick={() => setNotificationsOpen(true)}
                      endIcon={<Settings />}
                      variant='rounded'
                      sx={{
                        width: '100%', height: '39px', boxShadow: 'none !important',
                        display: 'flex', justifyContent: 'space-between', color: '#FFFFFF',
                        fontSize: '16px', fontWeight: '400', mt: 2, border: '1px solid #FFFFFF',
                        '&:hover': { bgcolor: 'primary.light', color: 'white', borderColor: 'primary.light', '.MuiButton-endIcon': { color: 'white' }, },
                        '.MuiButton-endIcon': { color: '#FFFFFF', '& .MuiSvgIcon-root': { fontSize: '24px' } },
                      }}
                    >
                      Notifications
                    </Button>
                  </Box> : ''
                }
              </List>

              <Divider sx={{ mt: 6, borderColor: '#F3F3F3' }} />
              <Box sx={{ width: '100%' }}>
                <LogoutButton />
              </Box>
              <Divider sx={{borderColor: '#F3F3F3'}}/>

            </Box>
          </Box>
          <Box sx={{ mt: 'auto', pb: 2 }}>
            <Legalese textColor='white'></Legalese>
          </Box>
        </Drawer>
      </Box>

      {/* dialogs moved to root return */}
    </>
  )

  return (
    <>
      {(isSmall || (!isAuthenticated && !pinToken) || pinPageWithNoPin || isNotPinPageAndNotAuthenticated) ? (
        <>
          {topNav}
          <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0 }}>
            {<Outlet/>}
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', minWidth: 0 }}>
          {leftNav}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {<Outlet/>}
          </Box>
        </Box>
      )}

      {/* DIALOGS (rendered regardless of layout) */}
      { globalOrderData ? <NotificationPreferenceStatusDialog open={notificationsOpen} setOpen={setNotificationsOpen}/> : '' }
      { globalOrderData ? <SmsPreferenceStatusDialog back={()=>{setNotificationsOpen(true)}} open={smsPreferenceStatusOpen} setOpen={setSmsPreferenceStatusOpen}/> : '' }
      {embeddedLink.Dialog}

    </>
  )
}

function HamburgerMenu({ dynamicLinks = [], onOpenLink }: { dynamicLinks?: PortalOrgUiConfigLink[]; onOpenLink?: (link: PortalOrgUiConfigLink) => void }) {
  const { search } = useLocation()
  const { closingId } = useParams()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const { Role = '', RoleOptions = [] } = globalOrderData || {}

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [roleExpanded, setRoleExpanded] = useState(false)
  const {setLogoutOpen, LogoutConfirmDialog} = useLogoutConfirm()

  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [smsPreferenceStatusOpen, setSmsPreferenceStatusOpen] = useState(false)

  const showNotificationsMenuItem = globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && !!globalOrderData?.Order.SmsPreferenceStatus?.phoneNumber

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setRoleExpanded(false)
  };

  return (
    <>
      <IconButton
        size='large'
        aria-label="menu"
        sx={{ m: '0 5px' }}
        onClick={handleClick}
      >
        <MenuIcon sx={{ width: '36px', height: 'auto', color: 'primary.main' }} />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            minWidth: '150px',
            borderRadius: '20px',
          }
        }}
      >
        {RoleOptions.length > 1 ? [
          <MenuItem
            key='role-label'
            onClick={() => setRoleExpanded((v: boolean) => !v)}
            sx={{ justifyContent: 'space-between' }}
          >
            {toTitleCase(humanize(Role))}
            <ListItemIcon sx={{ justifyContent: 'right' }}>
              {roleExpanded ? <ExpandLess sx={{ fontSize: 24 }} /> : <ExpandMore sx={{ fontSize: 24 }} />}
            </ListItemIcon>
          </MenuItem>,

          uniq(RoleOptions).map((role: string) => (
            <MenuItem
              key={role}
              disabled={role === Role}
              sx={{
                display: roleExpanded ? 'flex' : 'none',
                justifyContent: 'center',
                color: role === Role ? 'white' : 'inherit',
                '&.Mui-disabled': { backgroundColor: 'primary.light', opacity: 1 }
              }}
              onClick={() => {
                (window as Window).location = `/closings/${closingId}?role=${role}`
              }}
            >
              {toTitleCase(humanize(role))}
            </MenuItem>
          )),
          <Divider key='divider-roles' sx={{ borderColor: 'primary.main' }} />
        ]: ''}

        {showNotificationsMenuItem ?
          [
            <MenuItem
              key='notifications-button' sx={{ justifyContent: 'space-between'}}
              onClick={() => setNotificationsOpen(true)}
            >
              Notifications
              <ListItemIcon sx={{ justifyContent: 'right' }}>
                <Settings sx={{ fontSize: 24 }} />
              </ListItemIcon>
            </MenuItem>,
            <Divider key='divider-notifications' sx={{ borderColor: 'primary.main' }} />
          ] : ''
        }

        {dynamicLinks.map(link => {
          const iconEl = link.icon?.value
            ? <Icon className="material-icons-outlined" sx={{ fontSize: 24 }}>{link.icon.value}</Icon>
            : null;
          return ([
            <MenuItem
              key={link.url}
              onClick={() => {
                if (onOpenLink) {
                  onOpenLink(link);
                }
                handleClose();
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>{link.label}</Box>
              {iconEl && (
                <ListItemIcon sx={{ justifyContent: 'right', minWidth: 0, ml: 'auto' }}>
                  {iconEl}
                </ListItemIcon>
              )}
            </MenuItem>,
            <Divider key={`${link.url}-divider`} sx={{ borderColor: 'primary.main' }} />
          ]);
        })}

        <MenuItem
          key='terms-and-privacy-button'
          component={Link}
          to="/legal"
          onClick={handleClose}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>Terms and Privacy</Box>
          <ListItemIcon sx={{ justifyContent: 'right', minWidth: 0, ml: 'auto' }}>
            <DescriptionOutlined sx={{ fontSize: 24 }} />
          </ListItemIcon>
        </MenuItem>
        <Divider key='divider-terms-and-privacy' sx={{ borderColor: 'primary.main' }} />

        <MenuItem
          key='logout-button'
          onClick={() => {
            setLogoutOpen(true);
            handleClose();
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box component="span" sx={{ flexGrow: 1, minWidth: 0 }}>Logout</Box>
          <ListItemIcon sx={{ justifyContent: 'right', minWidth: 0, ml: 'auto' }}>
            <LogoutIcon sx={{ fontSize: 24 }} />
          </ListItemIcon>
        </MenuItem>
      </Menu>

      {/* DIALOGS */}
      { globalOrderData ? <NotificationPreferenceStatusDialog open={notificationsOpen} setOpen={setNotificationsOpen}/> : '' }
      { globalOrderData ? <SmsPreferenceStatusDialog back={()=>{setNotificationsOpen(true)}} open={smsPreferenceStatusOpen} setOpen={setSmsPreferenceStatusOpen}/> : '' }
      {LogoutConfirmDialog}
    </>
  )
}

function LogoutButton() {
  const {setLogoutOpen, LogoutConfirmDialog} = useLogoutConfirm()

  return (
    <>
      <Button onClick={() => setLogoutOpen(true)} sx={{
        color: 'white', width: 'inherit', p: 2, pt: 1, pb: 1,
        '&:hover': { backgroundColor: 'primary.light', color: 'white' },
      }}>
        Logout
      </Button>

      {LogoutConfirmDialog}
    </>
  )
}

function useLogoutConfirm() {
  const { isAuthenticated } = useAuth0()
  const [logoutOpen, setLogoutOpen] = useState(false)
  const { logout } = useAuth0()
  const { pinToken, setPinToken } = usePinTokenState()
  const isPinPage = useIsPinPage()

  const doLogout = () => {
    if (isPinPage || pinToken) {
      track('Logout', 'Manual logout pin')
      setPinToken(null)
    }
    if (isAuthenticated) {
      track('Logout', 'Manual logout')
      logout({ returnTo: `${window.location.origin}` })
    }
  }

  return {
    setLogoutOpen,
    LogoutConfirmDialog: (
      <Dialog
        key="logout"
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
      >
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => setLogoutOpen(false)}>No, I'll stay</Button>
          <Button variant="rounded" onClick={doLogout}>Yes, logout</Button>
        </DialogActions>
      </Dialog>
    )
  }
}

function CompanyLogo({ org }: {
	org: PortalDomainOrg;
}) {
  return (
    <Button
      component={NavLink}
      to="/closings"
      role="img"
      aria-label={`logo for ${org.CompanyDisplayName}`}
      sx={{
        p: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url("${org.LogoUrl}")`,
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: 'center',
        backgroundPositionY: 'center',
        backgroundSize: 'contain',
        '&:hover': {
          backgroundColor: 'initial',
        }
      }}
    ></Button>
  )
}
